import produce from 'immer';
import { isBoolean } from 'lodash';
import { getValues } from 'redux-form';
import { select, put } from 'redux-saga/effects';

import {
    ALL_STEPS,
    CHECK_ELIGIBILITY_STEP_ID
} from '@src/containers/Routes/helper/stepActiveIds.helper';

import { PUT_CUSTOMER_STATE_EDGE } from '../lendingAPI.actions';

// This file should make API requests of any kind but only to the EDGE endpoint
// As of July 2022, all future requests should aim to follow this format, to simplify the request process

export function* saveCustomerStateEdge(nextStep) {
    const formValues = yield select(({ form }) => getValues(form?.sign_up));
    const application = yield select(
        ({ lendingAPI }) => lendingAPI?.customer_state_edge?.data?.application
    );
    const ui = yield select(({ ui }) => ui);

    const newUI = produce(ui, (draft) => {
        draft.activeStep = ui.steps.find(
            (step) => step.id === CHECK_ELIGIBILITY_STEP_ID
        );

        const furthestStep = ALL_STEPS.find((step) => step.id === nextStep);

        draft.furthestStep = { id: furthestStep.id, name: furthestStep.name };
    });

    const newCustomerState = {
        data: {
            application: buildSerialisedNewCustomerState(
                application,
                formValues
            ),
            ui: newUI
        }
    };

    yield put({
        type: `api/${PUT_CUSTOMER_STATE_EDGE}`,
        body: JSON.stringify(newCustomerState)
    });
}

const buildSerialisedNewCustomerState = (application, formValues) => {
    return produce(application, (draft) => {
        const { type } = formValues.businessType.company;
        const {
            company_name,
            company_number,
            trading_as,
            industry,
            annual_turnover,
            monthly_turnover,
            annual_business_profit,
            online_sales_percentage,
            website,
            vat_registered_status,
            registered
        } = formValues.businessTypeFields.company;

        const { mm: month, yy: year } = registered;
        const combinedTradingFromDate = `${year}-${month}-01`;
        const companyTypes = {
            ltd: 'limited_liability_company',
            llp: 'limited_liability_partnership',
            st: 'sole_trader',
            op: 'ordinary_partnership',
            other: 'other',
            plc: 'public_limited_company'
        };

        const shownLimitedCompanyFields =
            type === 'ltd' || type === 'llp' || type === 'plc';

        const shownSoleTraderFields =
            type === 'st' || type === 'op' || type === 'other';

        draft.company = {};

        if (shownLimitedCompanyFields) {
            draft.company.registered_company_name = company_name;
            draft.company.company_number = company_number;
        }

        // Required check as trading name is optional in the form, but an empty string cannot be sent in request
        if (shownSoleTraderFields && trading_as) {
            draft.company.trading_name = trading_as;
        }

        if (shownSoleTraderFields) {
            draft.company.industry = industry;
            draft.company.last_12_months_profit = {
                amount: annual_business_profit
            };
        }

        // Required check as website is optional in the form but visible for both company types and
        // an empty string cannot be sent in request
        if (website) {
            website.match(/(http)(?!s)|(https)/g)
                ? (draft.company.website = website)
                : (draft.company.website = `https://${website}`);
        }

        draft.company.trading_from_date = combinedTradingFromDate;
        draft.company.last_12_months_turnover = { amount: annual_turnover };

        if (monthly_turnover) {
            draft.company.monthly_turnover = { amount: monthly_turnover };
        }

        draft.company.online_sales_percentage = online_sales_percentage;
        draft.company.type = companyTypes[type];

        if (isBoolean(vat_registered_status)) {
            draft.company.vat_status = {
                is_vat_registered: vat_registered_status
            };
        }
    });
};
